import { takeLatest, takeEvery, put, delay, call, select, spawn } from 'redux-saga/effects';
import { enqueueSnackbar } from '../../app/actions/appActions';
import {
    GET_USERS_GROUPS_REQUEST,
    DELETE_USER_GROUP_REQUEST,
    SEARCH_MEMBERS_REQUEST,
    GET_MUSIC_ON_HOLD_REQUEST,
    CREATE_USER_GROUP_REQUEST,
    GET_USER_GROUP_REQUEST,
    UPDATE_USER_GROUP_REQUEST,
    SEARCH_DEFAULT_EXTENSION_REQUEST
} from '../actions/groupsActionTypes';
import {
    setTotalUsersGroups,
    getUsersGroupsSuccess,
    getUsersGroupsFailure,
    deleteUserGroupSuccess,
    deleteUserGroupFailure,
    searchMembersSuccess,
    searchMembersFailure,
    setTotalMemberSuggestions,
    getMusicOnHoldSuccess,
    getMusicOnHoldFailure,
    createUserGroupSuccess,
    createUserGroupFailure,
    getUserGroupSuccess,
    getUserGroupFailure,
    setMembers,
    updateUserGroupsFailure,
    updateUserGroupsSuccess,
    searchDefaultExtensionSuccess,
    searchDefaultExtensionFailure,
    setTotalDefaultExtensions
} from '../actions/groupsActions';
import {
    getGroupsService,
    deleteGroupService,
    getMusicOnHoldService,
    createUserGroupService,
    getGroupByIDService,
    updateUserGroupService,
    uploadUserGroupPhoto
} from '../services/groupsServices';
import {
    getSearchText,
    getGroups,
    getGroupGeneralInformation,
    getGroupExpressConfiguration,
    getGroupUsersConfiguration
} from '../selectors/groupSelectors';
import { getUsersService } from '../../users/services/usersServices';
import { buildUserGroup, buildUserGroupUpdate } from '../helpers/buildUserGroups';
import { parseUserGroup, parseMembers } from '../helpers/parseUserGroups';

const createUserGroupTask = function* (action) {
    try {
        const { history } = action.payload;

        const generalInformation = yield select(getGroupGeneralInformation);
        const expressConfiguration = yield select(getGroupExpressConfiguration);
        const usersConfiguration = yield select(getGroupUsersConfiguration);

        const userGroup = buildUserGroup(generalInformation, expressConfiguration, usersConfiguration);

        const { data: { group: { id } } } = yield call(createUserGroupService, userGroup);

        if (userGroup.PictureURL) {
            yield spawn(uploadUserGroupPhoto, userGroup.PictureURL, id);
        }

        yield put(createUserGroupSuccess());
        yield put(enqueueSnackbar({
            message: 'groups:user_group_created',
            options: { variant: 'success' }
        }));

        history.push('/groups');
    } catch (error) {
        yield put(createUserGroupFailure());
        yield put(enqueueSnackbar({
            message: 'validations:ERROR',
            options: { variant: 'error' }
        }));
    }
};

const updateUserGroupTask = function* (action) {
    try {
        const { id, history } = action.payload;

        const generalInformation = yield select(getGroupGeneralInformation);
        const expressConfiguration = yield select(getGroupExpressConfiguration);
        const usersConfiguration = yield select(getGroupUsersConfiguration);

        const userGroup = buildUserGroupUpdate(generalInformation, expressConfiguration, usersConfiguration);

        yield call(updateUserGroupService, id, userGroup);

        if (userGroup.PictureURL) {
            yield spawn(uploadUserGroupPhoto, userGroup.PictureURL, id);
        }

        yield put(updateUserGroupsSuccess());
        yield put(enqueueSnackbar({
            message: 'groups:user_group_updated',
            options: { variant: 'success' }
        }));

        history.push('/groups');
    } catch (error) {
        yield put(updateUserGroupsFailure());
        yield put(enqueueSnackbar({
            message: 'validations:ERROR',
            options: { variant: 'error' }
        }));
    }
};

const getUserGroupTask = function* (action) {
    try {
        const { id } = action.payload;

        const { data: group } = yield call(getGroupByIDService, id);

        const parsedGroup = parseUserGroup(group[0]);
        const parsedMembers = parseMembers(group[0].Members);

        yield put(getUserGroupSuccess(parsedGroup));
        yield put(setMembers(parsedMembers));
    } catch (error) {
        yield put(getUserGroupFailure());
    }
};

const getGroupsTask = function* (action) {
    try {
        const { total, offset, searchText } = action.payload;

        if (searchText) {
            yield delay(500);
        }

        const { data } = yield call(getGroupsService, total, offset, 50, searchText);

        if (total) {
            yield put(setTotalUsersGroups(data.Total));
            yield put(getUsersGroupsSuccess(data.Groups));
        } else {
            yield put(getUsersGroupsSuccess(data));
        }
    } catch (error) {
        yield put(getUsersGroupsFailure());
    }
};

const deleteUserGroupTask = function* (action) {
    try {
        const { id } = action.payload;

        yield call(deleteGroupService, id);
        yield put(deleteUserGroupSuccess(id));

        const visibleUsersGroups = yield select(getGroups);
        const searchText = yield select(getSearchText);
        const { data } = yield call(getGroupsService, 0, visibleUsersGroups.length, 1, searchText);

        yield put(getUsersGroupsSuccess(data));
        yield put(enqueueSnackbar({
            message: 'groups:group_deleted',
            options: { variant: 'success' }
        }));
    } catch (error) {
        yield put(deleteUserGroupFailure(action.payload.id));
        yield put(enqueueSnackbar({
            message: 'validations:ERROR',
            options: { variant: 'error' }
        }));
    }
};

const searchMembersTask = function* (action) {
    try {
        const { total, offset, searchText } = action.payload;

        if (searchText) {
            yield delay(500);

            const { data } = yield call(getUsersService, total, offset, 50, searchText);

            if (total) {
                yield put(setTotalMemberSuggestions(data.Total));
                yield put(searchMembersSuccess(data.Users));
            } else {
                yield put(searchMembersSuccess(data));
            }
        } else {
            yield put(setTotalMemberSuggestions(0));
            yield put(searchMembersSuccess([]));
        }
    } catch (error) {
        yield put(searchMembersFailure());
    }
};

const searchDefaultExtensionTask = function* (action) {
    try {
        const { total, offset, searchText } = action.payload;

        if (searchText) {
            yield delay(500);

            const { data } = yield call(getUsersService, total, offset, 50, searchText);

            if (total) {
                yield put(setTotalDefaultExtensions(data.Total));
                yield put(searchDefaultExtensionSuccess(data.Users));
            } else {
                yield put(searchDefaultExtensionSuccess(data));
            }
        } else {
            yield put(setTotalDefaultExtensions(0));
            yield put(searchDefaultExtensionSuccess([]));
        }
    } catch (error) {
        yield put(searchDefaultExtensionFailure());
    }
}

const getMusicOnHoldTask = function* () {
    try {
        const { data: { result: mohs } } = yield call(getMusicOnHoldService);

        yield put(getMusicOnHoldSuccess(mohs));
    } catch (error) {
        yield put(getMusicOnHoldFailure());
    }
};

export default function* groupsSaga() {
    yield takeLatest(GET_USERS_GROUPS_REQUEST, getGroupsTask);
    yield takeEvery(DELETE_USER_GROUP_REQUEST, deleteUserGroupTask);
    yield takeLatest(SEARCH_MEMBERS_REQUEST, searchMembersTask);
    yield takeLatest(GET_MUSIC_ON_HOLD_REQUEST, getMusicOnHoldTask);
    yield takeLatest(CREATE_USER_GROUP_REQUEST, createUserGroupTask);
    yield takeLatest(GET_USER_GROUP_REQUEST, getUserGroupTask);
    yield takeLatest(UPDATE_USER_GROUP_REQUEST, updateUserGroupTask);
    yield takeLatest(SEARCH_DEFAULT_EXTENSION_REQUEST, searchDefaultExtensionTask);
};
